import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent, SyntheticEvent } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

interface SnapshotViewerProps {
  open: boolean;
  src: string;
  title: string;
  imageFailed: boolean;
  onOpenChange: (open: boolean) => void;
  onRetry: () => void;
  onImageError: () => void;
  onCloseFocus?: () => void;
  retrying?: boolean;
}

interface ScaleBounds {
  fit: number;
  max: number;
}

export function getSnapshotScaleBounds(
  naturalWidth: number,
  naturalHeight: number,
  viewportWidth: number,
  viewportHeight: number,
): ScaleBounds {
  if (naturalWidth <= 0 || naturalHeight <= 0 || viewportWidth <= 0 || viewportHeight <= 0) {
    return { fit: 1, max: 1 };
  }
  const fit = Math.min(1, viewportWidth / naturalWidth, viewportHeight / naturalHeight);
  return { fit, max: 1 };
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export default function SnapshotViewer({
  open,
  src,
  title,
  imageFailed,
  onOpenChange,
  onRetry,
  onImageError,
  onCloseFocus,
  retrying = false,
}: SnapshotViewerProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchStart = useRef<{ distance: number; scale: number } | null>(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [bounds, setBounds] = useState<ScaleBounds>({ fit: 1, max: 1 });
  const [scale, setScale] = useState(1);

  const measure = useCallback(() => {
    const stage = stageRef.current;
    if (!stage || !naturalSize.width || !naturalSize.height) return;
    const next = getSnapshotScaleBounds(
      naturalSize.width,
      naturalSize.height,
      stage.clientWidth,
      stage.clientHeight,
    );
    setBounds(next);
    setScale((current) => clamp(current, next.fit, next.max));
  }, [naturalSize]);

  useEffect(() => {
    if (!open) return;
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, open]);

  useEffect(() => {
    setNaturalSize({ width: 0, height: 0 });
    setBounds({ fit: 1, max: 1 });
    setScale(1);
    pointers.current.clear();
    pinchStart.current = null;
  }, [src, open]);

  const onImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    const nextSize = { width: image.naturalWidth, height: image.naturalHeight };
    setNaturalSize(nextSize);
    const stage = stageRef.current;
    if (stage) {
      const nextBounds = getSnapshotScaleBounds(
        nextSize.width,
        nextSize.height,
        stage.clientWidth,
        stage.clientHeight,
      );
      setBounds(nextBounds);
      setScale(nextBounds.fit);
    }
  };

  const zoomTo = (nextScale: number) => setScale(clamp(nextScale, bounds.fit, bounds.max));
  const zoomIn = () => zoomTo(Math.min(bounds.max, scale * 1.25));
  const zoomOut = () => zoomTo(Math.max(bounds.fit, scale / 1.25));
  const fitImage = () => zoomTo(bounds.fit);
  const originalSize = naturalSize.width > 0 && scale >= bounds.max - 0.001;
  const percentage = naturalSize.width > 0 ? Math.round(scale * 100) : 100;
  const canZoom = bounds.max > bounds.fit + 0.001;

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "touch") return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2) {
      const [first, second] = Array.from(pointers.current.values());
      pinchStart.current = {
        distance: Math.hypot(first.x - second.x, first.y - second.y),
        scale,
      };
    }
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "touch" || !pointers.current.has(event.pointerId)) return;
    const previous = pointers.current.get(event.pointerId)!;
    if (pointers.current.size === 1 && scale > bounds.fit) {
      event.currentTarget.scrollLeft += previous.x - event.clientX;
      event.currentTarget.scrollTop += previous.y - event.clientY;
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size !== 2 || !pinchStart.current) return;
    const [first, second] = Array.from(pointers.current.values());
    const distance = Math.hypot(first.x - second.x, first.y - second.y);
    if (pinchStart.current.distance > 0) {
      zoomTo(pinchStart.current.scale * (distance / pinchStart.current.distance));
    }
  };

  const onPointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[69] bg-black/90" />
      <DialogPrimitive.Content
        aria-describedby="snapshot-viewer-description"
        onCloseAutoFocus={(event) => {
          if (onCloseFocus) {
            event.preventDefault();
            onCloseFocus();
          }
        }}
        className="fixed inset-0 z-[70] flex h-[100dvh] w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-[#07100e] p-0 text-[#edf5f0] shadow-none sm:rounded-none"
      >
        <header className="relative z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-[#0a1714] px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6">
          <div className="min-w-0 w-full sm:w-auto sm:flex-1">
            <DialogPrimitive.Title className="truncate text-sm font-semibold text-[#edf5f0]">{title}</DialogPrimitive.Title>
            <DialogPrimitive.Description id="snapshot-viewer-description" className="mt-1 text-xs text-emerald-50/55">
              {imageFailed
                ? "Snapshot unavailable. Refresh the image to try again."
                : originalSize
                  ? "Original size · preview resolution limit"
                  : `${percentage}% of original size`}
            </DialogPrimitive.Description>
          </div>
          <div role="group" aria-label="Image zoom controls" className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              aria-label="Zoom out"
              onClick={zoomOut}
              disabled={!canZoom || scale <= bounds.fit + 0.001 || imageFailed}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-lg text-emerald-50 transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-300 disabled:cursor-not-allowed disabled:opacity-35"
            >
              −
            </button>
            <span className="min-w-[4.5rem] text-center text-[11px] font-medium tabular-nums text-emerald-50/65">
              {originalSize ? "Original size" : `${percentage}%`}
            </span>
            <button
              type="button"
              aria-label="Zoom in"
              onClick={zoomIn}
              disabled={!canZoom || scale >= bounds.max - 0.001 || imageFailed}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-lg text-emerald-50 transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-300 disabled:cursor-not-allowed disabled:opacity-35"
            >
              +
            </button>
            <button
              type="button"
              onClick={fitImage}
              disabled={!canZoom || scale <= bounds.fit + 0.001 || imageFailed}
              className="h-10 rounded-full border border-white/15 px-3 text-xs font-semibold text-emerald-50 transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-300 disabled:cursor-not-allowed disabled:opacity-35"
            >
              Fit
            </button>
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                aria-label="Close image viewer"
                className="ml-1 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-xl text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-300"
              >
                <span aria-hidden="true">×</span>
              </button>
            </DialogPrimitive.Close>
          </div>
        </header>
        <div
          ref={stageRef}
          className="relative flex min-h-0 flex-1 overflow-auto overscroll-contain"
          aria-busy={!imageFailed && !naturalSize.width}
          style={{ touchAction: "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
        >
          {!imageFailed && !naturalSize.width && (
            <span role="status" className="sr-only">Loading snapshot</span>
          )}
          <div className="flex min-h-full min-w-full shrink-0 items-center justify-center" style={{ width: naturalSize.width ? Math.max(0, naturalSize.width * scale) : "100%", height: naturalSize.height ? Math.max(0, naturalSize.height * scale) : "100%" }}>
          {imageFailed ? (
            <div className="flex max-w-sm flex-col items-center gap-3 rounded-2xl border border-white/10 bg-[#10211e] p-7 text-center">
              <p className="text-sm text-emerald-50/75">This camera image could not be loaded.</p>
              <button
                type="button"
                onClick={onRetry}
                disabled={retrying}
                className="rounded-lg border border-emerald-300/25 px-3 py-2 text-xs font-semibold text-emerald-100 transition hover:bg-emerald-300/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-300 disabled:cursor-wait disabled:opacity-55"
              >
                {retrying ? "Refreshing…" : "Refresh image"}
              </button>
            </div>
          ) : (
            <img
              key={src}
              src={src}
              alt={`Latest snapshot from ${title}`}
              onLoad={onImageLoad}
              onError={() => {
                setNaturalSize({ width: 0, height: 0 });
                onImageError();
              }}
              draggable={false}
              className="block h-auto w-auto max-w-none shrink-0 object-contain"
              style={naturalSize.width ? {
                width: naturalSize.width * scale,
                height: naturalSize.height * scale,
              } : { maxWidth: "100%", maxHeight: "100%" }}
            />
          )}
          </div>
        </div>
        <p className="shrink-0 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-1 text-center text-[11px] text-emerald-50/40">
          Preview image · zoom is limited to the image’s original pixel dimensions
        </p>
      </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
